import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import moment, { Moment } from "moment";
import "moment/locale/tr";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DebtType } from "../../Models/Credit";
import { getRemoteText, getRemoteValue } from "../../Utils/firebase";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";

const WEEKDAY_LABELS = ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"];

const PRIMARY_BLUE = "#191B1E";
const PAYMENT_DOT = "#FF8A00";

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateKey(d: Date): string {
  return moment(d).format("YYYY-MM-DD");
}

/**
 * Parses API ISO-8601 due dates to a local calendar day for badge matching.
 * Date-only strings (YYYY-MM-DD) use local midnight to avoid UTC off-by-one.
 */
function parseIsoPaymentDate(iso: string): Date | null {
  const s = iso.trim();
  if (!s) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const monthIndex = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    const parsed = new Date(y, monthIndex, day);
    if (
      parsed.getFullYear() === y &&
      parsed.getMonth() === monthIndex &&
      parsed.getDate() === day
    ) {
      return startOfDay(parsed);
    }
    return null;
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
}

export interface BulkSnoozeDateSelectionModalProps {
  open: boolean;
  onClosed: () => void;
  onPressed: (selectedDate: Date) => void;
  startDate: Date;
  endDate: Date;
  /** Due dates as ISO strings; parsed inside the modal for calendar badges. */
  paymentDates: string[];
  /**
   * When reopening (e.g. back from summary), restore this day and visible month.
   * Local calendar date as `YYYY-MM-DD`.
   */
  initialSelectedDateKey?: string;
  /**
   * CRL-3241: pending payments from the store; snoozable ones due on/before the
   * selected day are listed next to the calendar (informative only — the
   * authoritative preview still comes from `previewSnooze` on the next screen).
   */
  payments?: DebtType[];
}

function parseLocalDateKeyYMD(key: string): Date | null {
  const s = key.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  const parsed = new Date(y, monthIndex, day);
  if (
    parsed.getFullYear() !== y ||
    parsed.getMonth() !== monthIndex ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return startOfDay(parsed);
}

const BulkSnoozeDateSelectionModal = ({
  open,
  onClosed,
  onPressed,
  startDate,
  endDate,
  paymentDates,
  initialSelectedDateKey,
  payments,
}: BulkSnoozeDateSelectionModalProps) => {
  const rangeStart = useMemo(() => startOfDay(startDate), [startDate]);
  const rangeEnd = useMemo(() => endOfDay(endDate), [endDate]);

  const [viewMoment, setViewMoment] = useState(() =>
    moment(rangeStart).startOf("month")
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const paymentKeys = useMemo(() => {
    const set = new Set<string>();
    paymentDates.forEach((iso) => {
      const day = parseIsoPaymentDate(iso);
      if (day) set.add(dateKey(day));
    });
    return set;
  }, [paymentDates]);

  const syncSelection = useCallback(() => {
    if (rangeEnd < rangeStart) {
      setSelectedDate(null);
      return;
    }
    const now = startOfDay(new Date());
    let initial = rangeStart;
    if (now > rangeStart && now <= rangeEnd) {
      initial = now;
    }
    setSelectedDate(initial);
    setViewMoment(moment(initial).startOf("month"));
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    if (!open) return;

    const inRange = (d: Date) => {
      const t = startOfDay(d);
      return t >= rangeStart && t <= rangeEnd;
    };

    const key = initialSelectedDateKey?.trim();
    if (key) {
      const restored = parseLocalDateKeyYMD(key);
      if (restored && inRange(restored)) {
        setSelectedDate(restored);
        setViewMoment(moment(restored).startOf("month"));
        return;
      }
    }
    syncSelection();
  }, [open, initialSelectedDateKey, syncSelection, rangeStart, rangeEnd]);

  const isSelectable = useCallback(
    (day: Date) => {
      const t = startOfDay(day);
      return t >= rangeStart && t <= rangeEnd;
    },
    [rangeStart, rangeEnd]
  );

  const canGoPrev = useMemo(() => {
    const firstOfView = viewMoment.clone().startOf("month");
    return firstOfView.isAfter(moment(rangeStart).startOf("day"), "day");
  }, [viewMoment, rangeStart]);

  const canGoNext = useMemo(() => {
    const lastOfView = viewMoment.clone().endOf("month");
    return lastOfView.isBefore(moment(rangeEnd).startOf("day"), "day");
  }, [viewMoment, rangeEnd]);

  const calendarCells = useMemo(() => {
    const m = viewMoment.clone().locale("tr");
    const year = m.year();
    const month = m.month();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = (first.getDay() + 6) % 7;
    const cells: (number | null)[] = [];
    for (let i = 0; i < offset; i += 1) {
      cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push(d);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return { year, month, cells };
  }, [viewMoment]);

  const handleConfirm = () => {
    if (selectedDate && isSelectable(selectedDate)) {
      onPressed(startOfDay(selectedDate));
    }
  };

  /** Snoozable payments due on/before the selected day (all snoozable when no day yet). */
  const snoozablePayments = useMemo(() => {
    const list = (payments ?? []).filter((p) => p.snoozeAvailable);
    if (!selectedDate) return list;
    const limit = endOfDay(selectedDate);
    return list.filter((p) => {
      const due = parseIsoPaymentDate(p.dueDate);
      return due != null && due <= limit;
    });
  }, [payments, selectedDate]);

  const snoozableTotal = useMemo(
    () =>
      snoozablePayments.reduce(
        (sum, p) => sum + (p.remainingPaymentAmount || 0),
        0
      ),
    [snoozablePayments]
  );

  const hasPaymentsPanel = (payments ?? []).some((p) => p.snoozeAvailable);

  const title =
    getRemoteValue("bulk_snooze_select_day_title") ||
    "Erteleyeceğin Günü Seç";
  const legendText =
    getRemoteValue("bulk_snooze_payment_dates_legend") ||
    "Ödemelerinizin olduğu tarihleri gösterir";
  const buttonLabel =
    getRemoteValue("bulk_snooze_confirm_button") ||
    "Borçlarını Bu Güne Ertele";

  return (
    <Dialog
      open={open}
      onClose={onClosed}
      maxWidth={hasPaymentsPanel ? "md" : "sm"}
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          m: 2,
          maxWidth: hasPaymentsPanel ? "860px" : "575px",
          width: "100%",
        },
      }}
    >
      <Box sx={{ position: "relative", pt: 2.5, pb: 1, px: 2 }}>
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#191B1E",
            pr: 4,
            pl: 4,
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClosed}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[700],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3, px: { xs: 2, md: 3 }, pb: 3 }}>
        <Stack spacing={2} className='flex flex-col items-center justify-center'>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2.5, md: 4 }}
            sx={{ width: "100%", justifyContent: "center" }}
          >

          <Stack spacing={2} className='max-w-[400px] w-full place-self-center md:place-self-start'>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#191B1E",
                  textTransform: "capitalize",
                }}
              >
                {viewMoment.clone().locale("tr").format("MMMM YYYY")}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={!canGoPrev}
                  onClick={() =>
                    setViewMoment((vm: Moment) => vm.clone().subtract(1, "month"))
                  }
                  aria-label="previous month"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!canGoNext}
                  onClick={() =>
                    setViewMoment((vm: Moment) => vm.clone().add(1, "month"))
                  }
                  aria-label="next month"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {WEEKDAY_LABELS.map((label) => (
                  <Typography
                    key={label}
                    align="center"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "#94A3B8",
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: "6px",
                }}
              >
                {calendarCells.cells.map((day, idx) => {
                  if (day === null) {
                    return <Box key={`e-${idx}`} sx={{ height: 40 }} />;
                  }
                  const cellDate = new Date(
                    calendarCells.year,
                    calendarCells.month,
                    day
                  );
                  const selectable = isSelectable(cellDate);
                  const selected =
                    selectedDate != null && sameCalendarDay(cellDate, selectedDate);
                  const key = dateKey(cellDate);
                  const showPaymentDot = paymentKeys.has(key);

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 44,
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: 36,
                          height: 36,
                        }}
                      >
                        {showPaymentDot && (
                          <Box
                            aria-hidden
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              bgcolor: PAYMENT_DOT,
                              zIndex: 1,
                              pointerEvents: "none",
                            }}
                          />
                        )}
                        <Box
                          role="button"
                          tabIndex={selectable ? 0 : -1}
                          onClick={() => selectable && setSelectedDate(cellDate)}
                          onKeyDown={(e) => {
                            if (
                              selectable &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              setSelectedDate(cellDate);
                            }
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: selectable ? "pointer" : "default",
                            bgcolor: selected ? "#191B1E" : "transparent",
                            color: selected
                              ? "#fff"
                              : selectable
                                ? "#191B1E"
                                : "#CBD5E1",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            outline: "none",
                            "&:focus-visible": selectable
                              ? { boxShadow: "0 0 0 2px rgba(25,27,30,0.35)" }
                              : {},
                          }}
                        >
                          {day}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: PAYMENT_DOT,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>
                {legendText}
              </Typography>
            </Stack>

          </Stack>

          {/* CRL-3241: payments that will be snoozed for the selected day */}
          {hasPaymentsPanel && (
            <Stack
              sx={{
                flex: 1,
                minWidth: { md: "280px" },
                maxWidth: { md: "340px" },
                borderLeft: { md: "1px solid #F1F5F9" },
                borderTop: { xs: "1px solid #F1F5F9", md: "none" },
                pl: { md: 3 },
                pt: { xs: 2.5, md: 0 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} pb={1.5}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#191B1E" }}
                >
                  {getRemoteText("bulk_snooze_payments_title", "Ertelenecek ödemeler")}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#F1F5F9",
                    color: "#191B1E",
                    borderRadius: "999px",
                    minWidth: "22px",
                    height: "22px",
                    px: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  }}
                >
                  {snoozablePayments.length}
                </Box>
              </Stack>
              <Stack
                sx={{
                  overflowY: "auto",
                  maxHeight: { xs: "240px", md: "330px" },
                  pr: 0.5,
                }}
              >
                {snoozablePayments.map((p, idx) => (
                  <Stack
                    key={p.id}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      py: 1.5,
                      borderTop: idx === 0 ? "none" : "1px solid #F1F5F9",
                    }}
                  >
                    <img
                      src={p.shop.logoUrl}
                      alt={p.shop.name}
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "9px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#191B1E" }}
                      >
                        {p.shop.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                        {getRemoteText("due_date_prefix", "Vade")}{" "}
                        {moment(p.dueDate).locale("tr").format("D MMMM YYYY")}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#191B1E" }}
                    >
                      {FormatAmountSymbol(p.remainingPaymentAmount)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ borderTop: "1px solid #E2E8F0", pt: 1.5, mt: 0.5 }}
              >
                <Typography sx={{ fontSize: "0.85rem", color: "#64748B" }}>
                  {getRemoteText("total", "Toplam")}
                </Typography>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: 800, color: "#191B1E" }}
                >
                  {FormatAmountSymbol(snoozableTotal)}
                </Typography>
              </Stack>
            </Stack>
          )}

          </Stack>

          <div className="h-[20px]" />

          <Box className={hasPaymentsPanel ? 'w-full px-2' : 'max-w-[450px] w-full px-2'}>

            <Button
              fullWidth
              variant="contained"
              disabled={!selectedDate || !isSelectable(selectedDate)}
              onClick={handleConfirm}
              sx={{
                py: 1.25,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                bgcolor: PRIMARY_BLUE,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1E293B",
                  boxShadow: "none",
                },
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default BulkSnoozeDateSelectionModal;
